// src/utils/sessionManager.ts

/**
 * Obtiene el subdominio actual desde el hostname del navegador.
 * Ej: 'civil.algorimojuridico.com' -> 'civil'
 * Devuelve 'default' si no hay un subdominio claro (ej: localhost).
 */
const getSubdomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Si tenemos al menos 3 partes (ej: 'civil.dominio.com'), el primero es el subdominio.
  if (parts.length >= 3 && parts[0] !== 'www') {
    return parts[0];
  }

  // Si no, es un entorno de desarrollo o el dominio principal.
  return 'default';
};

// Obtenemos el prefijo una sola vez para usarlo en toda la sesión.
const KEY_PREFIX = getSubdomain();

// --- Funciones para generar las claves dinámicas ---
const getDynamicKey = (baseKey: string): string => `${KEY_PREFIX}_${baseKey}`;

// --- Funciones de la API pública de nuestro gestor ---

/**
 * Guarda los datos de la sesión en localStorage con claves específicas del subdominio.
 * @param data - El objeto de datos recibido de la API de login.
 */
export const saveSession = (data: any): void => {
  // Calcular la diferencia de días
  const fechaInicio = new Date(data.fecha_i);
  const fechaFin = new Date(data.fecha_f);
  const diffTime = Math.abs(fechaFin.getTime() - fechaInicio.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  localStorage.setItem(getDynamicKey('authToken'), data.access_token);
  localStorage.setItem(getDynamicKey('username'), data.username);
  localStorage.setItem(getDynamicKey('role'), data.role);
  localStorage.setItem(getDynamicKey('daysRemaining'), diffDays.toString());
};

/**
 * Carga los datos de la sesión desde localStorage, usando las claves del subdominio actual.
 * @returns Los datos de la sesión o un objeto con valores nulos si no se encuentra nada.
 */
export const loadSession = (): { token: string | null; username: string | null; role: string | null; daysRemaining: number | null } => {
  const token = localStorage.getItem(getDynamicKey('authToken'));
  const username = localStorage.getItem(getDynamicKey('username'));
  const role = localStorage.getItem(getDynamicKey('role'));
  const daysRemaining = localStorage.getItem(getDynamicKey('daysRemaining'));

  return {
    token,
    username,
    role,
    daysRemaining: daysRemaining ? parseInt(daysRemaining, 10) : null,
  };
};

/**
 * Limpia todos los datos de la sesión del subdominio actual de localStorage.
 */
export const clearSession = (): void => {
  localStorage.removeItem(getDynamicKey('authToken'));
  localStorage.removeItem(getDynamicKey('username'));
  localStorage.removeItem(getDynamicKey('role'));
  localStorage.removeItem(getDynamicKey('daysRemaining'));
};